import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, Users, Package, DollarSign, TrendingUp, ArrowRight, MapPin, Calendar } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { AdminNavbar } from '../components/admin/AdminNavbar';
import { Region } from '../types/models';
import { formatZAR } from '../utils/currency';
import { Link } from 'react-router-dom';
export function AdminHome() {
  const {
    user,
    users
  } = useAuth();
  const {
    orders,
    products
  } = useData();
  // Initialize region state based on user type
  const [selectedRegion, setSelectedRegion] = useState<Region>('All');
  useEffect(() => {
    if (user?.region && user.adminType !== 'main') {
      setSelectedRegion(user.region);
    }
  }, [user]);
  // Filter data based on selected region
  const filteredOrders = orders.filter(order => selectedRegion === 'All' ? true : order.userRegion === selectedRegion);
  const filteredProducts = products.filter(product => selectedRegion === 'All' ? true : product.region === selectedRegion || !product.region);
  // Calculate stats
  const totalRevenue = filteredOrders.reduce((sum, order) => sum + order.total, 0);
  const totalOrders = filteredOrders.length;
  const totalProducts = filteredProducts.length;
  const totalUsers = users.length; // Users are global for now, or could be filtered if user has region
  // Calculate recent activity (last 24h)
  const recentOrders = filteredOrders.filter(order => {
    const orderDate = new Date(order.date);
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return orderDate > oneDayAgo;
  });
  return <div className="min-h-screen bg-gray-50" data-id="element-1332">
      <AdminNavbar selectedRegion={selectedRegion} onRegionChange={user?.adminType === 'main' ? setSelectedRegion : undefined} data-id="element-1333" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1334">
        {/* Welcome Section */}
        <div className="mb-8" data-id="element-1335">
          <h1 className="text-3xl font-serif font-bold text-gray-900" data-id="element-1336">
            Welcome back, {user?.name}
          </h1>
          <p className="text-gray-600 mt-1 flex items-center gap-2" data-id="element-1337">
            Here's what's happening in
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${selectedRegion === 'Gauteng' ? 'bg-purple-100 text-purple-800' : selectedRegion === 'Limpopo' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1338">
              {selectedRegion === 'All' ? 'All Regions' : selectedRegion}
            </span>
            today.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-1339">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-1340">
            <div className="flex items-center justify-between mb-4" data-id="element-1341">
              <div className="p-3 bg-rose-50 rounded-xl text-rose-500" data-id="element-1342">
                <DollarSign className="w-6 h-6" data-id="element-1343" />
              </div>
              <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full flex items-center" data-id="element-1344">
                <TrendingUp className="w-3 h-3 mr-1" data-id="element-1345" />
                +12%
              </span>
            </div>
            <p className="text-sm text-gray-500 font-medium" data-id="element-1346">Total Revenue</p>
            <h3 className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1347">
              {formatZAR(totalRevenue)}
            </h3>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-1348">
            <div className="flex items-center justify-between mb-4" data-id="element-1349">
              <div className="p-3 bg-blue-50 rounded-xl text-blue-500" data-id="element-1350">
                <ShoppingBag className="w-6 h-6" data-id="element-1351" />
              </div>
              <span className="text-xs font-medium text-gray-500 bg-gray-50 px-2 py-1 rounded-full" data-id="element-1352">
                {recentOrders.length} new
              </span>
            </div>
            <p className="text-sm text-gray-500 font-medium" data-id="element-1353">Total Orders</p>
            <h3 className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1354">
              {totalOrders}
            </h3>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-1355">
            <div className="flex items-center justify-between mb-4" data-id="element-1356">
              <div className="p-3 bg-purple-50 rounded-xl text-purple-500" data-id="element-1357">
                <Users className="w-6 h-6" data-id="element-1358" />
              </div>
            </div>
            <p className="text-sm text-gray-500 font-medium" data-id="element-1359">Total Users</p>
            <h3 className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1360">
              {totalUsers}
            </h3>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-1361">
            <div className="flex items-center justify-between mb-4" data-id="element-1362">
              <div className="p-3 bg-amber-50 rounded-xl text-amber-500" data-id="element-1363">
                <Package className="w-6 h-6" data-id="element-1364" />
              </div>
            </div>
            <p className="text-sm text-gray-500 font-medium" data-id="element-1365">Active Products</p>
            <h3 className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1366">
              {totalProducts}
            </h3>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-1367">
          <Link to="/admin/products?action=add" className="group" data-id="element-1368">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all h-full" data-id="element-1369">
              <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center text-rose-600 mb-4 group-hover:scale-110 transition-transform" data-id="element-1370">
                <Package className="w-6 h-6" data-id="element-1371" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1372">
                Add New Product
              </h3>
              <p className="text-sm text-gray-500 mb-4" data-id="element-1373">
                Create a new product listing with images, pricing, and details.
              </p>
              <div className="flex items-center text-rose-600 text-sm font-medium" data-id="element-1374">
                Get Started{' '}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1375" />
              </div>
            </div>
          </Link>

          <Link to="/admin/users" className="group" data-id="element-1376">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all h-full" data-id="element-1377">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 mb-4 group-hover:scale-110 transition-transform" data-id="element-1378">
                <Users className="w-6 h-6" data-id="element-1379" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1380">
                Manage Users
              </h3>
              <p className="text-sm text-gray-500 mb-4" data-id="element-1381">
                View registered users, reset passwords, and manage permissions.
              </p>
              <div className="flex items-center text-blue-600 text-sm font-medium" data-id="element-1382">
                View Users{' '}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1383" />
              </div>
            </div>
          </Link>

          <Link to="/admin/orders" className="group" data-id="element-1384">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all h-full" data-id="element-1385">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600 mb-4 group-hover:scale-110 transition-transform" data-id="element-1386">
                <ShoppingBag className="w-6 h-6" data-id="element-1387" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1388">
                View Orders
              </h3>
              <p className="text-sm text-gray-500 mb-4" data-id="element-1389">
                Track and manage customer orders across all regions.
              </p>
              <div className="flex items-center text-purple-600 text-sm font-medium" data-id="element-1390">
                Go to Orders{' '}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1391" />
              </div>
            </div>
          </Link>
        </div>

        {/* Recent Activity Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-1392">
          <div className="p-6 border-b border-gray-100 flex justify-between items-center" data-id="element-1393">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-1394">Recent Orders</h3>
            <Link to="/admin/orders" className="text-sm text-rose-600 hover:text-rose-700 font-medium" data-id="element-1395">
              View All
            </Link>
          </div>
          <div className="overflow-x-auto" data-id="element-1396">
            <table className="w-full text-left" data-id="element-1397">
              <thead className="bg-gray-50" data-id="element-1398">
                <tr data-id="element-1399">
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1400">
                    Order ID
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1401">
                    Customer
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1402">
                    Date
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1403">
                    Region
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1404">
                    Status
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-1405">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-1406">
                {filteredOrders.slice(0, 5).map(order => <tr key={order.id} className="hover:bg-gray-50 transition-colors" data-id="element-1407">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-1408">
                      #{order.id.slice(-6)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1409">
                      {order.customerName}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500" data-id="element-1410">
                      <div className="flex items-center" data-id="element-1411">
                        <Calendar className="w-3 h-3 mr-1" data-id="element-1412" />
                        {new Date(order.date).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500" data-id="element-1413">
                      <div className="flex items-center" data-id="element-1414">
                        <MapPin className="w-3 h-3 mr-1" data-id="element-1415" />
                        {order.region}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-1416">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                        ${order.status === 'delivered' ? 'bg-green-100 text-green-800' : order.status === 'processing' ? 'bg-blue-100 text-blue-800' : order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1417">
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 text-right" data-id="element-1418">
                      {formatZAR(order.total)}
                    </td>
                  </tr>)}
                {filteredOrders.length === 0 && <tr data-id="element-1419">
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500" data-id="element-1420">
                      No orders found for this region.
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>;
}